import React, { useState, useEffect } from 'react';
import { Shield, Activity, LogIn, Database, AlertTriangle, Calendar, User, Search, Filter, CheckCircle, XCircle, Clock, TrendingUp, ArrowUp, ChevronLeft, ChevronRight, Eye, FileDown } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import AuditDetailsModal from '../Components/audit/AuditDetailsModal';
import OnlineUsersCard from '../Components/OnlineUsersCard';
import { ConfirmationDialog } from '../Components/ui/confirmation-dialog';
import { useToast } from '../Components/ui/use-toast';
import { translateOperation, translateAction, translateTable, translateResourceType, translateField, translateEventType, formatChangeValue } from '../Components/audit/auditFormatters';

// Paginação responsiva: 15 itens por página no desktop, 6 no mobile.
// Breakpoint alinhado ao `sm:` do Tailwind (640px), já usado em todo este arquivo.
const DESKTOP_PAGE_SIZE = 15;
const MOBILE_PAGE_SIZE = 6;
const MOBILE_MEDIA_QUERY = '(max-width: 639px)';
const getResponsivePageSize = () =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches
    ? MOBILE_PAGE_SIZE
    : DESKTOP_PAGE_SIZE;

const Audit = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('logs');
  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: getResponsivePageSize(), total: 0 });
  const [users, setUsers] = useState([]);
  const [resolvingEvent, setResolvingEvent] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  
  // Opções dinâmicas baseadas nos dados carregados
  const [availableOptions, setAvailableOptions] = useState({
    actions: [],
    resourceTypes: [],
    severities: [],
    eventTypes: ['actions', 'logins']
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    userId: '',
    startDate: '',
    endDate: '',
    action: '',
    resourceType: '',
    eventType: '', // 'all', 'actions', 'logins'
    success: '',
    severity: '',
    resolved: ''
  });

  // Carregar estatísticas e usuários
  useEffect(() => {
    loadStats();
    loadUsers();
  }, []);

  // Ajusta o tamanho da página ao cruzar o breakpoint mobile/desktop
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = (e) => {
      const newLimit = e.matches ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
      setPagination(prev => prev.limit === newLimit ? prev : { ...prev, limit: newLimit, page: 1 });
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Carregar dados quando tab ou filtros mudam
  useEffect(() => {
    loadData();
  }, [activeTab, pagination.page, filters]);

  const loadStats = async () => {
    try {
      const response = await axios.get('/api/audit/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/audit/users');
      setUsers(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit };

      // Adicionar filtros relevantes
      if (filters.search) params.search = filters.search;
      if (filters.userId) params.userId = filters.userId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      // Debug: verificar valores de data
      if (filters.startDate || filters.endDate) {
        console.log('🔍 [AUDIT] Filtros de data:', {
          startDate: filters.startDate,
          endDate: filters.endDate
        });
      }

      if (activeTab === 'logs') {
        // Para logs de auditoria, combinar dados de audit_logs e login_history
        if (filters.eventType === 'logins') {
          // Apenas logins
          if (filters.success) params.success = filters.success;
          const response = await axios.get('/api/audit/login-history', { params });
          
          let loginData = response.data.data.map(item => ({ ...item, type: 'login' }));
          let totalRecords = response.data.pagination.total;
          
          // Aplicar filtro de data no frontend (mesma lógica do modo "Todos")
          if (filters.startDate || filters.endDate) {
            const startDate = filters.startDate ? new Date(filters.startDate) : null;
            const endDate = filters.endDate ? new Date(filters.endDate) : null;
            
            loginData = loginData.filter(item => {
              const itemDate = new Date(item.created_at);
              if (startDate && itemDate < startDate) return false;
              if (endDate && itemDate > endDate) return false;
              return true;
            });
            
            totalRecords = loginData.length;
            console.log('🔍 [AUDIT] Filtro logins. Antes:', response.data.data.length, 'depois:', loginData.length);
          }
          
          setData(loginData);
          setPagination(prev => ({ ...prev, total: totalRecords }));
          updateAvailableOptions(loginData, 'logs');
        } else if (filters.eventType === 'actions') {
          // Apenas ações
          if (filters.action) params.action = filters.action;
          if (filters.resourceType) params.resourceType = filters.resourceType;
          const response = await axios.get('/api/audit/logs', { params });
          
          let actionData = response.data.data.map(item => ({ ...item, type: 'action' }));
          let totalRecords = response.data.pagination.total;
          
          // Aplicar filtro de data no frontend (mesma lógica do modo "Todos")
          if (filters.startDate || filters.endDate) {
            const startDate = filters.startDate ? new Date(filters.startDate) : null;
            const endDate = filters.endDate ? new Date(filters.endDate) : null;
            
            actionData = actionData.filter(item => {
              const itemDate = new Date(item.created_at);
              if (startDate && itemDate < startDate) return false;
              if (endDate && itemDate > endDate) return false;
              return true;
            });
            
            totalRecords = actionData.length;
            console.log('🔍 [AUDIT] Filtro ações. Antes:', response.data.data.length, 'depois:', actionData.length);
          }
          
          setData(actionData);
          setPagination(prev => ({ ...prev, total: totalRecords }));
          updateAvailableOptions(actionData, 'logs');
        } else {
          // Todos (buscar ambos, combinar e paginar no cliente)
          // Cada fonte já vem ordenada por created_at DESC — pedir "página 1, limit = page*limit"
          // de cada uma garante um superconjunto que cobre exatamente a página pedida depois do merge.
          const combinedFetchLimit = pagination.page * pagination.limit;

          const [logsResponse, loginsResponse] = await Promise.all([
            axios.get('/api/audit/logs', { params: { ...params, page: 1, limit: combinedFetchLimit } }),
            axios.get('/api/audit/login-history', { params: { ...params, page: 1, limit: combinedFetchLimit } })
          ]);
          
          let combined = [
            ...logsResponse.data.data.map(item => ({ ...item, type: 'action' })),
            ...loginsResponse.data.data.map(item => ({ ...item, type: 'login' }))
          ];

          // CRÍTICO: Se há filtro de data, aplicar filtro no frontend também
          // porque estamos combinando resultados de 2 endpoints diferentes
          let totalRecords = logsResponse.data.pagination.total + loginsResponse.data.pagination.total;
          
          if (filters.startDate || filters.endDate) {
            const startDate = filters.startDate ? new Date(filters.startDate) : null;
            const endDate = filters.endDate ? new Date(filters.endDate) : null;
            
            combined = combined.filter(item => {
              const itemDate = new Date(item.created_at);
              
              if (startDate && itemDate < startDate) return false;
              if (endDate && itemDate > endDate) return false;
              
              return true;
            });

            // ✅ CRÍTICO: Atualizar total para refletir registros APÓS filtro
            totalRecords = combined.length;

            console.log('🔍 [AUDIT] Filtro aplicado no frontend. Registros antes:', 
              logsResponse.data.data.length + loginsResponse.data.data.length,
              'depois:', combined.length,
              'total paginação:', totalRecords
            );
          }

          combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

          // Fatiar para a janela exata da página atual (o fetch acima trouxe um superconjunto)
          const pageStart = (pagination.page - 1) * pagination.limit;
          const pageSlice = combined.slice(pageStart, pageStart + pagination.limit);

          setData(pageSlice);
          setPagination(prev => ({
            ...prev,
            total: totalRecords  // ✅ Usa total correto (após filtro)
          }));
          updateAvailableOptions(combined, 'logs');
        }
      } else if (activeTab === 'changes') {
        const response = await axios.get('/api/audit/data-changes', { params });
        
        let changesData = response.data.data;
        let totalRecords = response.data.pagination.total;
        
        // Aplicar filtro de data no frontend
        if (filters.startDate || filters.endDate) {
          const startDate = filters.startDate ? new Date(filters.startDate) : null;
          const endDate = filters.endDate ? new Date(filters.endDate) : null;
          
          changesData = changesData.filter(item => {
            const itemDate = new Date(item.created_at);
            if (startDate && itemDate < startDate) return false;
            if (endDate && itemDate > endDate) return false;
            return true;
          });
          
          totalRecords = changesData.length;
          console.log('🔍 [AUDIT] Filtro alterações. Antes:', response.data.data.length, 'depois:', changesData.length);
        }
        
        setData(changesData);
        setPagination(prev => ({ ...prev, total: totalRecords }));
        updateAvailableOptions(changesData, 'changes');
      } else if (activeTab === 'security') {
        if (filters.severity) params.severity = filters.severity;
        if (filters.resolved) params.resolved = filters.resolved;
        const response = await axios.get('/api/audit/security-events', { params });
        
        let securityData = response.data.data;
        let totalRecords = response.data.pagination.total;
        
        // Aplicar filtro de data no frontend
        if (filters.startDate || filters.endDate) {
          const startDate = filters.startDate ? new Date(filters.startDate) : null;
          const endDate = filters.endDate ? new Date(filters.endDate) : null;
          
          securityData = securityData.filter(item => {
            const itemDate = new Date(item.created_at);
            if (startDate && itemDate < startDate) return false;
            if (endDate && itemDate > endDate) return false;
            return true;
          });
          
          totalRecords = securityData.length;
          console.log('🔍 [AUDIT] Filtro segurança. Antes:', response.data.data.length, 'depois:', securityData.length);
        }
        
        setData(securityData);
        setPagination(prev => ({ ...prev, total: totalRecords }));
        
        // Extrair opções únicas para filtros
        updateAvailableOptions(securityData, 'security');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para extrair opções únicas dos dados carregados
  const updateAvailableOptions = (dataArray, dataType) => {
    if (!dataArray || dataArray.length === 0) {
      setAvailableOptions({
        actions: [],
        resourceTypes: [],
        severities: [],
        eventTypes: []
      });
      return;
    }

    const newOptions = {
      actions: [],
      resourceTypes: [],
      severities: [],
      eventTypes: []
    };

    if (dataType === 'logs') {
      // Extrair ações únicas (apenas de items tipo 'action')
      const actions = [...new Set(
        dataArray
          .filter(item => item.type === 'action' && item.action)
          .map(item => item.action)
      )].sort();
      
      // Extrair tipos de recurso únicos
      const resourceTypes = [...new Set(
        dataArray
          .filter(item => item.type === 'action' && item.resource_type)
          .map(item => item.resource_type)
      )].sort();
      
      // Verificar se há logins e ações
      const hasLogins = dataArray.some(item => item.type === 'login');
      const hasActions = dataArray.some(item => item.type === 'action');
      
      const eventTypes = [];
      if (hasActions) eventTypes.push('actions');
      if (hasLogins) eventTypes.push('logins');

      newOptions.actions = actions;
      newOptions.resourceTypes = resourceTypes;
      newOptions.eventTypes = eventTypes;
    } else if (dataType === 'security') {
      // Extrair severidades únicas
      const severities = [...new Set(
        dataArray
          .filter(item => item.severity)
          .map(item => item.severity)
      )].sort();
      
      newOptions.severities = severities;
    }

    setAvailableOptions(newOptions);
    console.log('🔍 [AUDIT] Opções disponíveis atualizadas:', newOptions);
  };

  const openResolveDialog = (event) => {
    setResolutionNotes('');
    setResolvingEvent(event);
  };

  const closeResolveDialog = () => {
    if (resolving) return;
    setResolvingEvent(null);
    setResolutionNotes('');
  };

  const confirmResolveSecurityEvent = async () => {
    if (!resolvingEvent) return;
    setResolving(true);
    try {
      await axios.put(`/api/audit/security-events/${resolvingEvent.id}/resolve`, {
        resolution_notes: resolutionNotes
      });

      setResolvingEvent(null);
      setResolutionNotes('');
      loadData();
      loadStats();
    } catch (error) {
      console.error('Erro ao resolver evento:', error);
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao resolver evento'
      });
    } finally {
      setResolving(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      userId: '',
      startDate: '',
      endDate: '',
      action: '',
      resourceType: '',
      eventType: '',
      success: '',
      severity: '',
      resolved: ''
    });
  };

  const generatePDFReport = async () => {
    if (generatingPDF) return;
    
    if (data.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Nada para exportar',
        description: 'Não há registros para gerar o relatório'
      });
      return;
    }

    try {
      setGeneratingPDF(true);
      console.log('📄 [AUDIT] Gerando relatório PDF...');

      const response = await axios.post('/api/audit/generate-report', {
        records: data,
        filters: filters
      }, {
        responseType: 'arraybuffer',
        timeout: 120000 // 2 minutos
      });

      // Criar blob do PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Extrair nome do arquivo do header
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'relatorio_auditoria.pdf';
      
      console.log('📄 [FRONTEND] Content-Disposition:', contentDisposition);
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
          console.log('📄 [FRONTEND] Nome extraído e decodificado:', fileName);
        }
      } else {
        console.warn('⚠️ [FRONTEND] Header Content-Disposition não encontrado');
      }

      // Criar link temporário e disparar download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ [AUDIT] Relatório PDF gerado com sucesso:', fileName);
      
    } catch (error) {
      console.error('❌ [AUDIT] Erro ao gerar relatório PDF:', error);
      
      let errorMessage = 'Erro ao gerar relatório PDF';
      
      if (error.response?.data) {
        try {
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(error.response.data);
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Ignorar erro de parsing
        }
      }
      
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: errorMessage
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const goToPage = (page) => {
    setPagination(prev => ({ ...prev, page }));
    scrollToTop();
  };

  const goToPrevious = () => {
    if (pagination.page > 1) {
      goToPage(pagination.page - 1);
    }
  };

  const goToNext = () => {
    if (pagination.page < totalPages) {
      goToPage(pagination.page + 1);
    }
  };

  const tabs = [
    { id: 'logs', label: 'Logs de Auditoria', icon: Activity },
    { id: 'changes', label: 'Alterações de Dados', icon: Database },
    { id: 'security', label: 'Eventos de Segurança', icon: AlertTriangle }
  ];

  return (
    <div className="p-3 sm:p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between gap-2 sm:gap-3 mb-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">Auditoria do Sistema</h1>
              <p className="text-xs sm:text-sm md:text-base text-slate-600">Monitoramento e rastreamento de atividades</p>
            </div>
          </div>
          
          {/* Botão Gerar Relatório PDF */}
          <button
            onClick={generatePDFReport}
            disabled={generatingPDF || data.length === 0}
            className={`
              flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm
              transition-all duration-200
              ${generatingPDF || data.length === 0
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg'
              }
            `}
          >
            <FileDown className={`w-4 h-4 ${generatingPDF ? 'animate-bounce' : ''}`} />
            <span className="hidden sm:inline">
              {generatingPDF ? 'Gerando PDF...' : 'Gerar Relatório PDF'}
            </span>
            <span className="sm:hidden">PDF</span>
          </button>
        </div>
      </div>

      {/* Cards de Usuários (Online, Offline, Total) */}
      <OnlineUsersCard />

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Total de Logs</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.totalLogs.toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Logins</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.successfulLogins}</p>
                <p className="text-xs text-red-600">{stats.failedLogins} falhos</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <LogIn className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Alterações</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.totalDataChanges.toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Eventos de Segurança</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-600">{stats.unresolvedSecurityEvents}</p>
                <p className="text-xs text-slate-600">{stats.totalSecurityEvents} total</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-4 sm:mb-6">
        <div className="border-b border-slate-200">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Filtros */}
        <div className="p-3 sm:p-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-600" />
            <span className="text-sm sm:text-base font-medium text-slate-700">Filtros</span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3">
            {/* Busca */}
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filtro de Usuário */}
            <div>
              <select
                value={filters.userId}
                onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos os usuários</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Data e Hora Início */}
            <div>
              <input
                type="datetime-local"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                placeholder="Data/Hora Início"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Data e Hora Fim */}
            <div>
              <input
                type="datetime-local"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                placeholder="Data/Hora Fim"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filtros específicos por tab */}
            {activeTab === 'logs' && (
              <>
                <select
                  value={filters.eventType}
                  onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value }))}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os eventos</option>
                  {availableOptions.eventTypes.includes('actions') && (
                    <option value="actions">Apenas ações</option>
                  )}
                  {availableOptions.eventTypes.includes('logins') && (
                    <option value="logins">Apenas logins</option>
                  )}
                  {availableOptions.eventTypes.length === 0 && (
                    <option disabled>🔍 Nenhum evento neste período</option>
                  )}
                </select>

                {filters.eventType !== 'logins' && (
                  <>
                    <select
                      value={filters.action}
                      onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={availableOptions.actions.length === 0}
                    >
                      <option value="">Todas as ações</option>
                      {availableOptions.actions.length === 0 ? (
                        <option disabled>🔍 Nenhuma ação neste período</option>
                      ) : (
                        availableOptions.actions.map(action => (
                          <option key={action} value={action}>
                            {translateAction(action)}
                          </option>
                        ))
                      )}
                    </select>

                    <select
                      value={filters.resourceType}
                      onChange={(e) => setFilters(prev => ({ ...prev, resourceType: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={availableOptions.resourceTypes.length === 0}
                    >
                      <option value="">Todos os recursos</option>
                      {availableOptions.resourceTypes.length === 0 ? (
                        <option disabled>🔍 Nenhum recurso neste período</option>
                      ) : (
                        availableOptions.resourceTypes.map(resourceType => (
                          <option key={resourceType} value={resourceType}>
                            {translateResourceType(resourceType)}
                          </option>
                        ))
                      )}
                    </select>
                  </>
                )}

                {filters.eventType === 'logins' && (
                  <select
                    value={filters.success}
                    onChange={(e) => setFilters(prev => ({ ...prev, success: e.target.value }))}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todos</option>
                    <option value="true">Sucesso</option>
                    <option value="false">Falha</option>
                  </select>
                )}
              </>
            )}

            {activeTab === 'security' && (
              <>
                <select
                  value={filters.severity}
                  onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={availableOptions.severities.length === 0}
                >
                  <option value="">Todas as severidades</option>
                  {availableOptions.severities.length === 0 ? (
                    <option disabled>🔍 Nenhuma severidade neste período</option>
                  ) : (
                    availableOptions.severities.map(severity => (
                      <option key={severity} value={severity}>
                        {severity === 'low' ? 'Baixa' :
                         severity === 'medium' ? 'Média' :
                         severity === 'high' ? 'Alta' :
                         severity === 'critical' ? 'Crítica' :
                         severity}
                      </option>
                    ))
                  )}
                </select>

                <select
                  value={filters.resolved}
                  onChange={(e) => setFilters(prev => ({ ...prev, resolved: e.target.value }))}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos</option>
                  <option value="false">Pendentes</option>
                  <option value="true">Resolvidos</option>
                </select>
              </>
            )}

            {/* Botão Limpar */}
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12 text-sm sm:text-base text-slate-500">
              Nenhum registro encontrado
            </div>
          ) : (
            <>
              {/* Tabela de Logs de Auditoria (inclui ações e logins) */}
              {activeTab === 'logs' && (
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden">
                      <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Data/Hora</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Tipo</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Usuário</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden lg:table-cell">Evento</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden xl:table-cell">Detalhes</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden md:table-cell">Localização</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden sm:table-cell">IP</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((log, index) => (
                        <tr key={`${log.type}-${log.id}-${index}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm text-slate-600">{formatDate(log.created_at)}</td>
                          <td className="py-3 px-4">
                            {log.type === 'login' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <LogIn className="w-3 h-3" />
                                Login
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                <Activity className="w-3 h-3" />
                                Ação
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-900">
                            {log.user?.name || log.email || 'Sistema'}
                          </td>
                          <td className="py-3 px-4 hidden lg:table-cell">
                            {log.type === 'login' ? (
                              log.success ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3" />
                                  Login bem-sucedido
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  <XCircle className="w-3 h-3" />
                                  Login falhou
                                </span>
                              )
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                {translateAction(log.action)}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600 hidden xl:table-cell">
                            {log.type === 'login'
                              ? (log.failure_reason || 'Login realizado com sucesso')
                              : (log.description || `${translateAction(log.action)} ${translateResourceType(log.resource_type)}`)
                            }
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500 hidden md:table-cell">
                            {(() => {
                              const metadata = log.new_values || {};
                              const parts = [];
                              if (metadata.city) parts.push(metadata.city);
                              if (metadata.region) parts.push(metadata.region);
                              if (metadata.country) parts.push(metadata.country);
                              return parts.length > 0 ? parts.join(', ') : '-';
                            })()}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500 hidden sm:table-cell">{log.ip_address || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center">
                              <button
                                onClick={() => setSelectedItem(log)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 hover:border-purple-300 transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Ver Detalhes</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Tabela de Alterações de Dados */}
              {activeTab === 'changes' && (
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden">
                      <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Data/Hora</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Usuário</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden md:table-cell">Tabela</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Operação</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden lg:table-cell">Campo</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden xl:table-cell">Valor Anterior</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((change, index) => (
                        <tr key={`change-${change.id}-${index}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm text-slate-600">{formatDate(change.created_at)}</td>
                          <td className="py-3 px-4 text-sm text-slate-900">{change.user?.name || 'Sistema'}</td>
                          <td className="py-3 px-4 text-sm text-slate-600 hidden md:table-cell">{translateTable(change.table_name)}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              change.operation === 'INSERT' ? 'bg-green-100 text-green-700' :
                              change.operation === 'UPDATE' ? 'bg-blue-100 text-blue-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {translateOperation(change.operation)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600 hidden lg:table-cell">{change.field_name ? translateField(change.field_name) : '-'}</td>
                          <td className="py-3 px-4 text-sm text-slate-500 max-w-xs truncate hidden xl:table-cell">{formatChangeValue(change.field_name, change.old_value, '-')}</td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center">
                              <button
                                onClick={() => setSelectedItem(change)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 hover:border-purple-300 transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Ver Detalhes</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Tabela de Eventos de Segurança */}
              {activeTab === 'security' && (
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden">
                      <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Data/Hora</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden md:table-cell">Usuário</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden lg:table-cell">Tipo</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Severidade</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden xl:table-cell">Descrição</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 hidden sm:table-cell">Status</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((event, index) => (
                        <tr key={`event-${event.id}-${index}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm text-slate-600">{formatDate(event.created_at)}</td>
                          <td className="py-3 px-4 text-sm text-slate-900 hidden md:table-cell">{event.user?.name || '-'}</td>
                          <td className="py-3 px-4 text-sm text-slate-600 hidden lg:table-cell">{translateEventType(event.event_type)}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              event.severity === 'critical' ? 'bg-red-100 text-red-700' :
                              event.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                              event.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {event.severity === 'critical' ? 'Crítica' :
                               event.severity === 'high' ? 'Alta' :
                               event.severity === 'medium' ? 'Média' : 'Baixa'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600 hidden xl:table-cell">{event.description}</td>
                          <td className="py-3 px-4 hidden sm:table-cell">
                            {event.resolved ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3" />
                                Resolvido
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                <Clock className="w-3 h-3" />
                                Pendente
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => setSelectedItem(event)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 hover:border-purple-300 transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Ver Detalhes</span>
                              </button>
                              {!event.resolved && (
                                <button
                                  onClick={() => openResolveDialog(event)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-colors"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  <span className="hidden lg:inline">Resolver</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex flex-col items-center gap-4 mt-6 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    {/* Botão Anterior */}
                    <button
                      onClick={goToPrevious}
                      disabled={pagination.page === 1}
                      className="flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Anterior</span>
                    </button>

                    {/* Números das páginas */}
                    <div className="flex items-center gap-1">
                      {/* Primeira página (se atual > 3) */}
                      {pagination.page > 3 && (
                        <>
                          <button
                            onClick={() => goToPage(1)}
                            className={`w-10 h-10 flex items-center justify-center text-sm border rounded-lg transition-colors ${
                              1 === pagination.page
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            1
                          </button>
                          {pagination.page > 4 && <span className="px-2 text-slate-400">...</span>}
                        </>
                      )}

                      {/* Páginas próximas (±2) */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => page >= pagination.page - 2 && page <= pagination.page + 2)
                        .map(page => (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`w-10 h-10 flex items-center justify-center text-sm border rounded-lg transition-colors ${
                              page === pagination.page
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}

                      {/* Última página (se atual < total - 2) */}
                      {pagination.page < totalPages - 2 && (
                        <>
                          {pagination.page < totalPages - 3 && <span className="px-2 text-slate-400">...</span>}
                          <button
                            onClick={() => goToPage(totalPages)}
                            className={`w-10 h-10 flex items-center justify-center text-sm border rounded-lg transition-colors ${
                              totalPages === pagination.page
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </div>

                    {/* Botão Próximo */}
                    <button
                      onClick={goToNext}
                      disabled={pagination.page === totalPages}
                      className="flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span className="hidden sm:inline">Próximo</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Contador embaixo */}
                  <div className="text-xs sm:text-sm text-slate-600 text-center">
                    Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} registros
                  </div>
                </div>
              )}

              {/* Botão Voltar ao Topo */}
              {data.length > 0 && (
                <div className="flex justify-end mt-6">
                  <button
                    onClick={scrollToTop}
                    className="flex items-center gap-2 px-4 py-2 text-sm sm:text-base text-blue-700 hover:text-blue-800 bg-white/80 backdrop-blur-sm border-2 border-blue-200 hover:border-blue-400 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-pulse hover:animate-none"
                  >
                    <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
                    Voltar ao Topo
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de Detalhes */}
      {selectedItem && (
        <AuditDetailsModal
          item={selectedItem}
          type={activeTab}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* Diálogo de Resolução de Evento de Segurança */}
      <ConfirmationDialog
        isOpen={!!resolvingEvent}
        onClose={closeResolveDialog}
        onConfirm={confirmResolveSecurityEvent}
        title="Resolver evento de segurança"
        description={resolvingEvent ? `${translateEventType(resolvingEvent.event_type)} — ${resolvingEvent.description || 'Sem descrição'}` : ''}
        confirmText={resolving ? 'Resolvendo...' : 'Marcar como resolvido'}
        cancelText="Cancelar"
        confirmDisabled={resolving}
      >
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Notas sobre a resolução (opcional)
        </label>
        <textarea
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
          placeholder="Ex.: IP verificado, tentativa bloqueada pelo firewall..."
          rows={3}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          disabled={resolving}
        />
      </ConfirmationDialog>
    </div>
  );
};

export default Audit;
